import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { apiClient } from '../services/api'
import { useCurrentUser } from '../hooks/useAuth'
import SEOHead from '../components/SEOHead'
import type { Pet, Appointment } from '../types'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SPECIES = ['dog', 'cat', 'rabbit', 'bird', 'reptile', 'other'] as const

function fmt(priceCents: number) {
  return `$${(priceCents / 100).toFixed(2)}`
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
  })
}

function fmtTime(iso: string) {
  return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
}

const STATUS_STYLES: Record<string, string> = {
  pending:   'bg-yellow-100 text-yellow-800',
  confirmed: 'bg-green-100  text-green-800',
  completed: 'bg-gray-100   text-gray-600',
  cancelled: 'bg-red-100    text-red-700',
}

// ─── Section: Profile info ────────────────────────────────────────────────────

function ProfileSection() {
  const qc = useQueryClient()
  const { data: user } = useCurrentUser()
  const [editing, setEditing] = useState(false)
  const [form, setForm] = useState({ first_name: '', last_name: '', phone: '' })
  const [saved, setSaved] = useState(false)

  function startEdit() {
    setForm({ first_name: user?.first_name ?? '', last_name: user?.last_name ?? '', phone: user?.phone ?? '' })
    setEditing(true)
    setSaved(false)
  }

  const updateMutation = useMutation({
    mutationFn: async () => (await apiClient.patch('/profile', { user: form })).data,
    onSuccess: (updated) => {
      qc.setQueryData(['currentUser'], updated)
      setEditing(false)
      setSaved(true)
    },
  })

  if (!user) return null

  return (
    <div className="card space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Personal info</h2>
        {!editing && (
          <button onClick={startEdit} className="text-sm text-brand-600 hover:underline font-medium">
            Edit
          </button>
        )}
      </div>

      {saved && (
        <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">
          Profile updated successfully.
        </p>
      )}

      {editing ? (
        <form
          onSubmit={(e) => { e.preventDefault(); updateMutation.mutate() }}
          className="space-y-4"
        >
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="label">First name</label>
              <input className="input" required value={form.first_name}
                onChange={(e) => setForm((p) => ({ ...p, first_name: e.target.value }))} />
            </div>
            <div>
              <label className="label">Last name</label>
              <input className="input" required value={form.last_name}
                onChange={(e) => setForm((p) => ({ ...p, last_name: e.target.value }))} />
            </div>
          </div>
          <div>
            <label className="label">Phone</label>
            <input className="input" type="tel" value={form.phone}
              onChange={(e) => setForm((p) => ({ ...p, phone: e.target.value }))} />
          </div>
          {updateMutation.isError && (
            <p className="text-sm text-red-600">Failed to update. Please try again.</p>
          )}
          <div className="flex gap-2 pt-1">
            <button type="submit" disabled={updateMutation.isPending} className="btn-primary text-sm px-5 py-2">
              {updateMutation.isPending ? 'Saving…' : 'Save changes'}
            </button>
            <button type="button" onClick={() => setEditing(false)} className="btn-secondary text-sm px-5 py-2">
              Cancel
            </button>
          </div>
        </form>
      ) : (
        <dl className="grid sm:grid-cols-2 gap-x-8 gap-y-4 text-sm">
          {[
            { label: 'First name',    value: user.first_name },
            { label: 'Last name',     value: user.last_name  },
            { label: 'Email address', value: user.email      },
            { label: 'Phone',         value: user.phone || '—' },
          ].map(({ label, value }) => (
            <div key={label}>
              <dt className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-0.5">{label}</dt>
              <dd className="text-gray-900 font-medium">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  )
}

// ─── Section: Pets ────────────────────────────────────────────────────────────

function PetsSection() {
  const qc = useQueryClient()

  const { data: pets = [], isLoading } = useQuery<Pet[]>({
    queryKey: ['pets'],
    queryFn: async () => (await apiClient.get<Pet[]>('/pets')).data,
  })

  const [showAdd, setShowAdd] = useState(false)
  const [newPet, setNewPet] = useState({ name: '', species: 'dog', breed: '' })
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState({ name: '', species: 'dog', breed: '' })

  const addMutation = useMutation({
    mutationFn: async () => (await apiClient.post<Pet>('/pets', { pet: newPet })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pets'] }); setShowAdd(false); setNewPet({ name: '', species: 'dog', breed: '' }) },
  })

  const updateMutation = useMutation({
    mutationFn: async (id: number) => (await apiClient.patch<Pet>(`/pets/${id}`, { pet: editForm })).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['pets'] }); setEditingId(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: async (id: number) => apiClient.delete(`/pets/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['pets'] }),
  })

  function startEdit(pet: Pet) {
    setEditingId(pet.id)
    setEditForm({ name: pet.name, species: pet.species, breed: pet.breed ?? '' })
  }

  return (
    <div className="card space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">My pets</h2>
        {!showAdd && (
          <button onClick={() => setShowAdd(true)} className="text-sm text-brand-600 hover:underline font-medium">
            + Add pet
          </button>
        )}
      </div>

      {isLoading ? (
        <div className="space-y-2">
          {[1, 2].map((i) => <div key={i} className="h-14 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      ) : (
        <ul className="space-y-3">
          {pets.map((pet) => (
            <li key={pet.id} className="border border-gray-200 rounded-xl overflow-hidden">
              {editingId === pet.id ? (
                <form
                  onSubmit={(e) => { e.preventDefault(); updateMutation.mutate(pet.id) }}
                  className="p-4 space-y-3 bg-gray-50"
                >
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <label className="label">Name</label>
                      <input className="input" required value={editForm.name}
                        onChange={(e) => setEditForm((p) => ({ ...p, name: e.target.value }))} />
                    </div>
                    <div>
                      <label className="label">Species</label>
                      <select className="input" value={editForm.species}
                        onChange={(e) => setEditForm((p) => ({ ...p, species: e.target.value }))}>
                        {SPECIES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label">Breed</label>
                      <input className="input" value={editForm.breed}
                        onChange={(e) => setEditForm((p) => ({ ...p, breed: e.target.value }))} />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button type="submit" disabled={updateMutation.isPending} className="btn-primary text-xs px-4 py-1.5">
                      {updateMutation.isPending ? 'Saving…' : 'Save'}
                    </button>
                    <button type="button" onClick={() => setEditingId(null)} className="btn-secondary text-xs px-4 py-1.5">
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <div className="flex items-center justify-between px-4 py-3">
                  <div>
                    <p className="font-medium text-gray-900 text-sm">{pet.name}</p>
                    <p className="text-xs text-gray-400 capitalize">
                      {pet.species}{pet.breed ? ` · ${pet.breed}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-3">
                    <button onClick={() => startEdit(pet)} className="text-xs text-brand-600 hover:underline">Edit</button>
                    <button
                      onClick={() => { if (confirm(`Remove ${pet.name}?`)) deleteMutation.mutate(pet.id) }}
                      className="text-xs text-red-500 hover:underline"
                    >
                      Remove
                    </button>
                  </div>
                </div>
              )}
            </li>
          ))}

          {pets.length === 0 && !showAdd && (
            <li className="text-center py-8 text-gray-400 text-sm">
              No pets added yet.{' '}
              <button onClick={() => setShowAdd(true)} className="text-brand-600 hover:underline">Add your first pet →</button>
            </li>
          )}
        </ul>
      )}

      {showAdd && (
        <form
          onSubmit={(e) => { e.preventDefault(); addMutation.mutate() }}
          className="border border-brand-200 bg-brand-50 rounded-xl p-4 space-y-3"
        >
          <p className="text-sm font-medium text-gray-700">Add a new pet</p>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="label">Name *</label>
              <input className="input" required value={newPet.name}
                onChange={(e) => setNewPet((p) => ({ ...p, name: e.target.value }))} />
            </div>
            <div>
              <label className="label">Species *</label>
              <select className="input" value={newPet.species}
                onChange={(e) => setNewPet((p) => ({ ...p, species: e.target.value }))}>
                {SPECIES.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Breed</label>
              <input className="input" value={newPet.breed}
                onChange={(e) => setNewPet((p) => ({ ...p, breed: e.target.value }))} />
            </div>
          </div>
          <div className="flex gap-2">
            <button type="submit" disabled={!newPet.name || addMutation.isPending} className="btn-primary text-sm px-5 py-2">
              {addMutation.isPending ? 'Saving…' : 'Save pet'}
            </button>
            <button type="button" onClick={() => setShowAdd(false)} className="btn-secondary text-sm px-5 py-2">
              Cancel
            </button>
          </div>
          {addMutation.isError && <p className="text-xs text-red-600">Failed to save. Please try again.</p>}
        </form>
      )}
    </div>
  )
}

// ─── Section: Appointments ────────────────────────────────────────────────────

function AppointmentsSection() {
  const qc = useQueryClient()

  const { data: appointments = [], isLoading } = useQuery<Appointment[]>({
    queryKey: ['appointments'],
    queryFn: async () => (await apiClient.get<Appointment[]>('/appointments')).data,
  })

  const cancelMutation = useMutation({
    mutationFn: async (id: number) => apiClient.delete(`/appointments/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['appointments'] }),
  })

  return (
    <div className="card space-y-5">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-gray-900">Upcoming appointments</h2>
        <Link to="/book" className="text-sm text-brand-600 hover:underline font-medium">
          + Book a visit
        </Link>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          {[1, 2].map((i) => <div key={i} className="h-20 bg-gray-100 animate-pulse rounded-xl" />)}
        </div>
      ) : appointments.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm">
          No upcoming appointments.{' '}
          <Link to="/book" className="text-brand-600 hover:underline">Book a visit →</Link>
        </div>
      ) : (
        <ul className="space-y-3">
          {appointments.map((appt) => (
            <li key={appt.id} className="border border-gray-200 rounded-xl p-4">
              <div className="flex items-start justify-between gap-4">
                <div className="space-y-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="font-semibold text-gray-900 text-sm">{appt.service.name}</p>
                    <span className={`text-xs font-medium px-2 py-0.5 rounded-full capitalize ${STATUS_STYLES[appt.status] ?? 'bg-gray-100 text-gray-600'}`}>
                      {appt.status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    {fmtDate(appt.scheduled_at)} at {fmtTime(appt.scheduled_at)}
                    {appt.pet && <> · <span className="capitalize">{appt.pet.name}</span></>}
                  </p>
                  {appt.address && (
                    <p className="text-xs text-gray-400">
                      {appt.address.street}, {appt.address.city}, {appt.address.state} {appt.address.zip}
                    </p>
                  )}
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-brand-700 font-bold text-sm">{fmt(appt.service.price_cents)}</p>
                  {appt.status === 'pending' || appt.status === 'confirmed' ? (
                    <button
                      onClick={() => { if (confirm('Cancel this appointment?')) cancelMutation.mutate(appt.id) }}
                      className="text-xs text-red-500 hover:underline mt-1"
                    >
                      Cancel
                    </button>
                  ) : null}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ProfilePage() {
  const { data: user, isLoading } = useCurrentUser()

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 rounded-full border-4 border-brand-300 border-t-brand-600 animate-spin" />
      </div>
    )
  }

  if (!user) {
    return (
      <div className="max-w-lg mx-auto px-4 py-20 text-center">
        <p className="text-gray-500 mb-4">Please sign in to view your profile.</p>
        <Link to="/login" className="btn-primary px-6 py-3">Sign In</Link>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-12 space-y-6">
      <SEOHead title="My Account" description="Manage your profile, pets, and appointments." noIndex />

      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-1">My Account</h1>
        <p className="text-gray-500 text-sm">Manage your profile, pets, and appointments.</p>
      </div>

      <ProfileSection />
      <PetsSection />
      <AppointmentsSection />
    </div>
  )
}
